import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Button, Card, Form, InputGroup, Spinner, Table } from 'react-bootstrap';
import {
  GET_VANITY_DUPLICATE_COMPENSATION_CONFIG,
  UPDATE_VANITY_DUPLICATE_COMPENSATION_CONFIG,
} from '../graphql';

type VanityConfig = {
  sticker: number[];
  profileFrame: number[];
  appSkin: number[];
  badge: number[];
  emoji: number[];
  emojiPack: number[];
};

type ConfigKey = keyof VanityConfig;

const DEFAULT_CONFIG: VanityConfig = {
  sticker: [70],
  profileFrame: [100, 200, 400],
  appSkin: [200, 400, 800],
  badge: [200],
  emoji: [25],
  emojiPack: [500],
};

const ROWS: Array<{ key: ConfigKey; label: string; levels: number }> = [
  { key: 'sticker', label: 'Sticker', levels: 1 },
  { key: 'profileFrame', label: 'Profile Frame', levels: 3 },
  { key: 'appSkin', label: 'App Skin', levels: 3 },
  { key: 'badge', label: 'Badge', levels: 1 },
  { key: 'emoji', label: 'Emoji', levels: 1 },
  { key: 'emojiPack', label: 'Emoji Pack', levels: 1 },
];

const cloneConfig = (config: VanityConfig): VanityConfig => ({
  sticker: [...config.sticker],
  profileFrame: [...config.profileFrame],
  appSkin: [...config.appSkin],
  badge: [...config.badge],
  emoji: [...config.emoji],
  emojiPack: [...config.emojiPack],
});

const configsEqual = (left: VanityConfig, right: VanityConfig): boolean =>
  ROWS.every(
    ({ key }) =>
      left[key].length === right[key].length &&
      left[key].every((value, index) => value === right[key][index])
  );

const VanityDuplicateCompensationScreen = () => {
  const { data, loading, error } = useQuery(GET_VANITY_DUPLICATE_COMPENSATION_CONFIG, {
    fetchPolicy: 'network-only',
  });
  const [saveConfig] = useMutation(UPDATE_VANITY_DUPLICATE_COMPENSATION_CONFIG);
  const [config, setConfig] = useState<VanityConfig>(cloneConfig(DEFAULT_CONFIG));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!saved) {
      return undefined;
    }

    const timeout = window.setTimeout(() => setSaved(false), 30_000);
    return () => window.clearTimeout(timeout);
  }, [saved]);

  const serverConfig = data?.liveOps?.vanityDuplicateCompensationConfig as VanityConfig | undefined;
  useEffect(() => {
    if (serverConfig) {
      setConfig(cloneConfig(serverConfig));
    }
  }, [serverConfig]);

  const isValid = useMemo(
    () =>
      ROWS.every(({ key, levels }) => {
        const values = config[key];
        return values.length === levels && values.every((value) => Number.isInteger(value) && value >= 0);
      }),
    [config]
  );
  const isDirty = useMemo(
    () => (serverConfig ? !configsEqual(config, serverConfig) : false),
    [config, serverConfig]
  );

  const updatePrice = (key: ConfigKey, level: number, rawValue: string) => {
    const value = Number(rawValue);
    setConfig((current) => {
      const prices = [...current[key]];
      prices[level] = value;
      return { ...current, [key]: prices };
    });
    setSaved(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!isDirty) {
      return;
    }
    if (!isValid) {
      setSaveError('All values must be non-negative whole numbers.');
      return;
    }
    setSaving(true);
    setSaved(false);
    try {
      await saveConfig({
        variables: { input: cloneConfig(config) },
        refetchQueries: [{ query: GET_VANITY_DUPLICATE_COMPENSATION_CONFIG }],
        awaitRefetchQueries: true,
      });
      setSaved(true);
      setSaveError(null);
    } catch (mutationError) {
      setSaveError(mutationError instanceof Error ? mutationError.message : 'Unable to save game config.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Vanity Duplicate Compensation</h5>
        </Card.Header>
        <Card.Body>
          {error ? <Alert variant="danger">{error.message}</Alert> : null}
          {saveError ? <Alert variant="danger">{saveError}</Alert> : null}
          {saved ? <Alert variant="success">Configuration saved.</Alert> : null}

          {loading && !serverConfig ? (
            <Spinner animation="border" />
          ) : (
            <>
              <Table bordered hover responsive size="sm">
                <thead>
                  <tr>
                    <th className="text-center">Vanity item</th>
                    <th className="text-center">Level 1</th>
                    <th className="text-center">Level 2</th>
                    <th className="text-center">Level 3</th>
                  </tr>
                </thead>
                <tbody>
                  {ROWS.map(({ key, label, levels }) => (
                    <tr key={key}>
                      <td className="align-middle font-weight-bold">{label}</td>
                      {[0, 1, 2].map((level) => (
                        <td key={level} className="align-middle">
                          {level < levels ? (
                            <InputGroup size="sm" style={{ width: 140 }}>
                              <Form.Control
                                type="number"
                                min={0}
                                step={1}
                                value={Number.isFinite(config[key][level]) ? config[key][level] : ''}
                                disabled={saving}
                                isInvalid={
                                  !Number.isInteger(config[key][level]) || config[key][level] < 0
                                }
                                onChange={(event) =>
                                  updatePrice(key, level, event.currentTarget.value)
                                }
                              />
                              <InputGroup.Append>
                                <InputGroup.Text
                                  className="px-2"
                                  title="Diamonds"
                                  aria-label="Diamonds"
                                >
                                  💎
                                </InputGroup.Text>
                              </InputGroup.Append>
                            </InputGroup>
                          ) : (
                            <span className="text-muted">Not applicable</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>

              <div className="d-flex justify-content-end">
                <Button
                  variant="primary"
                  disabled={saving || !isValid || !isDirty}
                  onClick={handleSave}
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default VanityDuplicateCompensationScreen;
